import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import { ALL_CASES as CASES, CASE_STATS, CASE_SITES, CASE_GUIDE } from "@/data/caseStudies";
import { ARTICLES } from "@/data/blogArticles";
import { shortName } from "@/data/testimonials";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";
import CaseStatChips from "@/components/CaseStatChips";
import SiteFrame from "@/components/SiteFrame";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const index = CASES.findIndex((c) => c.slug === slug);
  if (index === -1) return <Navigate to="/" replace />;
  const c = CASES[index];
  const guideArticle = ARTICLES.find((a) => a.slug === CASE_GUIDE[c.slug]);
  const prev = CASES[(index - 1 + CASES.length) % CASES.length];
  const next = CASES[(index + 1) % CASES.length];

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  const siteImg = CASE_SITES[c.slug]?.img;
  const caseSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${c.client} Case Study`,
    description: `${c.client} (${c.industry}): ${c.after}`,
    ...(siteImg ? { image: `https://www.zddesignagency.com${siteImg}` } : {}),
    mainEntityOfPage: `https://www.zddesignagency.com/case-studies/${c.slug}`,
    url: `https://www.zddesignagency.com/case-studies/${c.slug}`,
    publisher: {
      "@type": "Organization",
      name: "ZD Design",
      url: "https://www.zddesignagency.com/",
    },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.zddesignagency.com/" },
      { "@type": "ListItem", position: 2, name: "Case Studies", item: "https://www.zddesignagency.com/case-studies" },
      { "@type": "ListItem", position: 3, name: c.client, item: `https://www.zddesignagency.com/case-studies/${c.slug}` },
    ],
  };

  return (
    <main data-testid="case-study-page">
      <Seo
        title={`${c.client} Case Study — ZD Design`}
        description={`${c.client} (${c.industry}): ${c.after} A real ZD Design case study, in the client's own words.`}
        path={`/case-studies/${c.slug}`}
        ogImage={siteImg}
        ogImageAlt={`${c.client} website project by ZD Design`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(caseSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-20 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Link
              to="/case-studies"
              data-testid="case-study-back-link"
              className="inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All case studies
            </Link>
            <Eyebrow>Case study {c.num} — {c.industry}</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              {c.client}
            </h1>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-slate-200 bg-coal/70 px-4 py-1.5 font-mono text-[13px] uppercase tracking-[0.12em] text-mist"
                >
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              {c.intro}
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div data-testid="case-study-site-frame-wrap" className="mx-auto mt-12 max-w-4xl">
              <SiteFrame
                site={CASE_SITES[c.slug]}
                client={c.client}
                industry={c.industry}
                testId="case-study-site-frame"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid gap-6 lg:grid-cols-2">
            <FadeUp>
              <div
                data-testid="case-study-before"
                className="h-full rounded-2xl border border-slate-200 bg-coal/60 p-8"
              >
                <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
                  The business problem
                </div>
                <p className="mt-4 text-lg leading-relaxed text-mist">
                  {c.before}
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div
                data-testid="case-study-after"
                className="h-full rounded-2xl border border-flame/30 bg-coal/60 p-8"
              >
                <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                  After
                </div>
                <p className="mt-4 text-lg leading-relaxed text-slate-700">
                  {c.after}
                </p>
              </div>
            </FadeUp>
          </div>

          {!c.story && (
            <FadeUp delay={0.12}>
              <div data-testid="case-study-results" className="mt-8">
                <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
                  Results
                </div>
                <CaseStatChips
                  items={CASE_STATS[c.slug]}
                  testPrefix={`case-study-chip-${c.slug}`}
                  className="mt-4"
                />
              </div>
            </FadeUp>
          )}

          <FadeUp delay={0.15}>
            <h2 className="mt-16 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              What changed.
            </h2>
            <div className="mt-8 space-y-4">
              {c.steps.map((s, i) => (
                <div
                  key={s}
                  data-testid={`case-study-step-${i + 1}`}
                  className="flex items-start gap-4 rounded-xl border border-slate-200 bg-coal/70 px-6 py-5"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame" />
                  <p className="text-lg leading-relaxed text-slate-700">{s}</p>
                </div>
              ))}
            </div>
          </FadeUp>

          {c.story && (
            <FadeUp delay={0.2}>
              <h2 className="mt-16 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                The measured outcome.
              </h2>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {c.story.stats.map((s) => (
                  <div
                    key={s.big}
                    data-testid={`case-study-stat-${s.big.replace(/[^a-zA-Z0-9]/g, "")}`}
                    className="rounded-2xl border border-flame/30 bg-coal/60 p-8 text-center"
                  >
                    <div className="font-display text-5xl font-extrabold text-flame md:text-6xl">
                      {s.big}
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {s.small}
                    </div>
                    <div className="mt-1 font-mono text-[13px] uppercase tracking-[0.18em] text-faint">
                      {s.period}
                    </div>
                  </div>
                ))}
              </div>
              <div
                data-testid="case-study-metric-counts"
                className="mt-6 rounded-2xl border border-slate-200 bg-coal/70 p-8"
              >
                <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
                  What the metric counts
                </div>
                <p className="mt-3 text-lg leading-relaxed text-mist">
                  {c.story.counts}
                </p>
              </div>
            </FadeUp>
          )}
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          {c.quote && (
            <FadeUp>
              <blockquote
                data-testid="case-study-quote"
                className="mx-auto max-w-3xl border-l-2 border-flame/50 pl-6 text-xl italic leading-relaxed text-slate-700 md:text-2xl"
              >
                &ldquo;{c.quote}&rdquo;
                <span className="mt-3 block font-mono text-base not-italic text-faint">
                  — {shortName(c.name)}, {c.role}
                </span>
              </blockquote>
            </FadeUp>
          )}

          {guideArticle && (
            <FadeUp delay={0.1}>
              <Link
                to={`/blog/${guideArticle.slug}`}
                data-testid="case-study-guide-link"
                className="group mt-10 flex items-center justify-between gap-5 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/40 md:p-7"
              >
                <span>
                  <span className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                    Related guide
                  </span>
                  <span className="mt-2 block font-display text-xl font-bold tracking-tight text-slate-900">
                    {guideArticle.title}
                  </span>
                  {guideArticle.excerpt && (
                    <span className="mt-1 block text-base leading-snug text-faint">
                      {guideArticle.excerpt}
                    </span>
                  )}
                </span>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-flame transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </FadeUp>
          )}

          <FadeUp delay={0.1}>
            <div className="mt-16 rounded-2xl border border-slate-200 bg-obsidian p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Want a turnaround like this?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and
                talk directly with Todd.
              </p>
              <button
                data-testid="case-study-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {[
                { c: prev, label: "Previous case study" },
                { c: next, label: "Next case study" },
              ].map(({ c: n, label }) => (
                <Link
                  key={label}
                  to={`/case-studies/${n.slug}`}
                  data-testid={`case-study-nav-${label.includes("Previous") ? "prev" : "next"}`}
                  className="group rounded-2xl border border-slate-200 bg-coal/70 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/40"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[13px] uppercase tracking-[0.2em] text-faint">
                      {label}
                    </span>
                    <ArrowRight
                      className={`h-4 w-4 text-flame transition-transform duration-300 ${
                        label.includes("Previous")
                          ? "rotate-180 group-hover:-translate-x-1"
                          : "group-hover:translate-x-1"
                      }`}
                    />
                  </div>
                  <div className="mt-3 font-display text-xl font-bold text-slate-900">
                    {n.client}
                  </div>
                </Link>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
